import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {type VariantProps} from 'class-variance-authority';
import {useTranslation} from 'react-i18next';
import {cn} from '../lib/cn';
import {buttonVariants} from './buttonVariants';

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      asChild = false,
      readOnly = false,
      loading = false,
      loadingText,
      disabled,
      children,
      type,
      ...props
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const showLoadingLabel = size !== 'icon';
    const isDisabled = readOnly || disabled || loading;
    const classNames = cn(buttonVariants({variant, size, shape, readOnly}), className);

    if (asChild) {
      return (
        <Slot
          ref={ref}
          aria-disabled={isDisabled || undefined}
          className={classNames}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        disabled={isDisabled}
        className={classNames}
        {...props}
      >
        {loading ? (
          <>
            <Spinner />
            {showLoadingLabel ? <span>{loadingText || t('please_wait')}</span> : null}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        d="M12 2a10 10 0 0 1 10 10"
      />
    </svg>
  );
}
